import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { InspectionService } from '../../../inspection/inspection.service';
import { EmailTemplateService } from '../../email-template.service';
import { Inspection, ProductType } from '../../interfaces';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-send-email-modal',
  templateUrl: './send-email-modal.component.html',
  styleUrls: ['./send-email-modal.component.scss'],
})
export class SendEmailModalComponent implements OnInit {
  constructor(
    public router: Router,
    private emailTemplateService: EmailTemplateService,
    private inspectionService: InspectionService,
    private sanitizer: DomSanitizer,
    private fb: UntypedFormBuilder,
    private toastr: ToastrService,
  ) {}

  @ViewChild('sendEmailModal', { static: true }) public sendEmailModal: ModalDirective;
  @ViewChild('gjs', { static: true }) private gjs: ElementRef;
  @ViewChildren('editor') editor: QueryList<EditorComponent>;
  @Input() customerUrl: string = '';
  @Input() inspection: Inspection;
  @Input() letterId: number;
  @Input() productType: ProductType;
  @Output() onSend = new EventEmitter<any>();
  emailContents: SafeHtml[] = [];

  form: UntypedFormGroup;
  fieldChanges = {
    subject: false,
    cc: false,
    to: false,
  };
  initialValues: any;
  tinyInit = {
    menubar: false,
    inline: true,
    placeholder: 'Add text custom block',
    forced_root_block: 'div',
    plugins: ['lists', 'paste'],
    toolbar: 'undo redo | bold italic underline | numlist bullist | clearContent',
    paste_word_valid_elements: 'div[style],strong/b,em,span[style],a[href],ul,ol,li,h1,h2,h3,h4,h5,h6,p',
    valid_elements: 'div[style],strong/b,em,span[style],a[href],ul,ol,li,p',
    fixed_toolbar_container: '#toolbar-container',
    content_style: '.mce-content-body div { margin-top: 0 !important; font-family: sans-serif; font-size: 15px;}',
    valid_styles: {
      '*': 'font-size,font-family,color,text-decoration,text-align',
    },
    base_url: '/assets/tinymce', // This should be the base URL where TinyMCE is located
    paste_preprocess: (plugin, args) => {
      for (let i = 1; i <= 6; i++) {
        const regexOpen = new RegExp(`<h${i}>`, 'g');
        const regexClose = new RegExp(`</h${i}>`, 'g');
        args.content = args.content.replace(regexOpen, '<div><b>').replace(regexClose, '</b></div></br>');
      }
    },
    font_formats: 'sans-serif; Times New Roman=times new roman,times,serif;',
    setup: editor => {
      this.editorSetup(editor);
      editor.ui.registry.addButton('clearContent', {
        icon: 'remove',
        tooltip: 'Clear Content',
        onAction: () => {
          editor.setContent('');
          editor.focus();
        },
      });
      editor.on('focus', function () {
        editor.getBody().style.minHeight = '60px';
        editor.getBody().style.paddingLeft = '1px';
      });
      editor.on('blur', function () {
        editor.getBody().style.minHeight = 'auto';
      });
    },
  };

  editorSetup(editor) {
    editor.on('focus', () => {
      if (editor && editor.container) {
        editor.container.style.zIndex = '1000000';
      }
    });
  }

  _customField: string = '';

  get customField() {
    return this._customField;
  }
  set customField(value: string) {
    this._customField = value;
    this.previewEmail();
  }

  ngOnInit() {
    this.form = this.fb.group(
      {
        subject: ['', [Validators.required]],
        cc: ['', [this.emailListValidator()]],
        to: ['', [Validators.required, this.emailListValidator()]],
        customField: [''],
      },
      { validators: [this.emailCrossFieldValidator()] },
    );
  }

  async previewEmail() {
    const emailTemplates = await this.emailTemplateService.getRecLetter({
      split: true,
      customerUrl: this.customerUrl,
      customField: this.customField,
      inspectionId: this.inspection.id,
    });

    // Map the HTML contents directly to the array, skipping the fourth one
    this.emailContents = emailTemplates.html
      .map((htmlContent: string, index: number) =>
        index !== 3 ? this.sanitizer.bypassSecurityTrustHtml(htmlContent) : null,
      )
      .filter(content => content !== null); // Filter out the null values
  }

  getValidationClass(field: string) {
    const control = this.form.get(field);
    return {
      'is-valid': control.valid && (control.dirty || control.touched),
      'is-invalid': control.invalid && (control.dirty || control.touched),
    };
  }

  async show() {
    this.sendEmailModal.show();
    this.previewEmail();
    this.setInfo();
  }

  setInfo() {
    this.initialValues = {
      subject: `Inspection Report - ${this.inspection?.customerName} - ${this.inspection?.policyNumber}`,
      cc: this.inspection?.policy?.broker?.email,
      to: this.inspection?.contactEmail,
    };
    this.form.patchValue(this.initialValues);
    this.form.markAsUntouched();
    this.form.markAsPristine();
    this.trackValueChanges();
  }

  private trackValueChanges() {
    Object.keys(this.initialValues).forEach(fieldName => {
      this.form.get(fieldName)?.valueChanges.subscribe(newValue => {
        this.fieldChanges[fieldName] = newValue !== this.initialValues[fieldName];
      });
    });
  }

  resetControlToDefault(controlName: string) {
    if (this.initialValues.hasOwnProperty(controlName)) {
      this.form.get(controlName).setValue(this.initialValues[controlName]);
      this.fieldChanges[controlName] = false;
    }
  }
  emailListValidator() {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const trimmedValue = control.value ? control.value.trim() : '';
      if (!trimmedValue) {
        return null;
      }

      const emails = trimmedValue
        .split(',')
        .map(e => e.trim())
        .filter(e => e.length > 0);

      const invalidEmails = emails.filter(e => !this.validateEmail(e));
      const uniqueEmails = new Set(emails);
      const hasDuplicates = uniqueEmails.size < emails.length;

      if (invalidEmails.length) {
        return { invalidEmails: { value: control.value, invalidEmails } };
      }
      if (hasDuplicates) {
        return { duplicateEmails: { value: control.value } };
      }
      return null;
    };
  }

  emailCrossFieldValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      if (group instanceof FormGroup) {
        const ccEmails = group
          .get('cc')
          ?.value.split(',')
          .map(email => email.trim().toLowerCase());
        const toEmails = group
          .get('to')
          ?.value.split(',')
          .map(email => email.trim().toLowerCase());
        const intersection = ccEmails.filter(email => toEmails.includes(email));

        if (intersection.length > 0) {
          return { emailsCrossMatch: true };
        }
      }
      return null;
    };
  }

  private validateEmail(email: string): boolean {
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return emailRegex.test(email);
  }

  cancel() {
    this.sendEmailModal.hide();
    this.form.get('customField').setValue('');
  }

  sendEmail() {
    this.sendEmailModal.hide();
    const { to, cc, subject, customField } = this.form.value;
    this.inspectionService
      .sendRecommendationLetterEmail(to, cc, subject, this.customerUrl, customField, this.inspection.id, this.letterId)
      .then(() => {
        this.toastr.success('You have successfully sent a rec letter.');
        this.onSend.emit({ SendEmail: true });
      });
  }
}
